import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { JoinRequestFormData, FormState, Question, QuestionType } from '../types';
import { validateForm, hasErrors, formatDate } from '../utils/validation';
import { submitJoinRequest, fetchQuestions } from '../services/api';
import './JoinRequestForm.css';

interface JoinRequestFormProps {
  onSuccess?: (data: any) => void;
  onError?: (error: string) => void;
}

const JoinRequestForm: React.FC<JoinRequestFormProps> = ({ onSuccess, onError }) => {
  const [formState, setFormState] = useState<FormState>('idle');
  const [serverError, setServerError] = useState<string>('');
  const [successData, setSuccessData] = useState<any>(null);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [isLoadingQuestions, setIsLoadingQuestions] = useState(true);
  const [groups, setGroups] = useState<string[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<string | null>(null);
  const [groupError, setGroupError] = useState<string>('');

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
    setError,
    clearErrors
  } = useForm<JoinRequestFormData>({
    mode: 'onBlur',
    defaultValues: {
      fullName: '',
      birthDate: '',
      phoneNumber: '',
      answers: []
    }
  });

  // Fetch questions on component mount
  useEffect(() => {
    const loadQuestions = async () => {
      try {
        const fetchedQuestions = await fetchQuestions();
        setQuestions(fetchedQuestions);
        // Derive groups and set selection when only one exists
        const uniqueGroups = Array.from(new Set(
          fetchedQuestions.map(q => q.group).filter((g): g is string => Boolean(g))
        ));
        setGroups(uniqueGroups);

        // Initialize selected group and answers
        if (uniqueGroups.length === 0) {
          // No groups - initialize answers for all questions
          const initialAnswers = fetchedQuestions.map(q => ({
            questionId: q.id,
            answer: q.type === QuestionType.BOOLEAN ? false : ''
          }));
          setValue('answers', initialAnswers);
        } else if (uniqueGroups.length === 1) {
          const onlyGroup = uniqueGroups[0];
          setSelectedGroup(onlyGroup);
          const initialAnswers = fetchedQuestions
            .filter(q => q.group === onlyGroup)
            .map(q => ({
              questionId: q.id,
              answer: q.type === QuestionType.BOOLEAN ? false : ''
            }));
          setValue('answers', initialAnswers);
        } else {
          // Wait for user to select a group before initializing answers
          setValue('answers', []);
        }
        // Initialize answers array with empty values
        // Note: Answer initialization for all questions moved to group-specific logic above
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'فشل في تحميل الأسئلة';
        setServerError(errorMessage);
        onError?.(errorMessage);
      } finally {
        setIsLoadingQuestions(false);
      }
    };

    loadQuestions();
  }, [setValue, onError]);

  // Re-initialize answers whenever the selected group changes
  useEffect(() => {
    if (isLoadingQuestions) return;
    
    // If no groups exist, initialize answers for all questions
    if (groups.length === 0) {
      const initialAnswers = questions.map(q => ({
        questionId: q.id,
        answer: q.type === QuestionType.BOOLEAN ? false : ''
      }));
      setValue('answers', initialAnswers);
      return;
    }
    
    if (!selectedGroup) {
      setValue('answers', []);
      return;
    }

    const initialAnswersForGroup = questions
      .filter(q => q.group === selectedGroup)
      .map(q => ({
        questionId: q.id,
        answer: q.type === QuestionType.BOOLEAN ? false : ''
      }));
    setValue('answers', initialAnswersForGroup);
  }, [selectedGroup, questions, isLoadingQuestions, setValue, groups.length]);

  const onSubmit = async (data: JoinRequestFormData) => {
    // Clear any previous errors that might block submission
    clearErrors(); // Clear all react-hook-form errors
    setServerError(''); // Clear server errors
    
    // Require group selection when multiple groups exist
    if (groups.length > 1 && !selectedGroup) {
      setGroupError('يرجى اختيار المجموعة لعرض الأسئلة والإجابة عليها');
      return;
    }
    
    // Validate required questions
    const validationErrors = validateForm(data);
    
    if (hasErrors(validationErrors)) {
      // Set validation errors in the form
      if (validationErrors.fullName) {
        setError('fullName', { type: 'manual', message: validationErrors.fullName });
      }
      if (validationErrors.birthDate) {
        setError('birthDate', { type: 'manual', message: validationErrors.birthDate });
      }
      if (validationErrors.phoneNumber) {
        setError('phoneNumber', { type: 'manual', message: validationErrors.phoneNumber });
      }
      return;
    }

    // Additional validation for required questions
    const visibleQuestions = selectedGroup
      ? questions.filter(q => q.group === selectedGroup)
      : questions;
    const requiredQuestions = visibleQuestions.filter(q => q.isRequired);
    
    const hasUnansweredRequired = requiredQuestions.some(q => {
      const answer = data.answers.find(a => a.questionId === q.id);
      let isUnanswered = false;
      
      if (!answer) {
        isUnanswered = true;
      } else if (q.type === QuestionType.BOOLEAN) {
        // For boolean questions, check if answer is null or undefined
        isUnanswered = answer.answer === null || answer.answer === undefined;
      } else if (q.type === QuestionType.TEXT) {
        // For text questions, check if answer is empty or just whitespace
        const answerValue = String(answer.answer || '');
        isUnanswered = answerValue.trim() === '';
      } else {
        // For other types, check if answer is null, undefined, or empty
        isUnanswered = answer.answer === null || answer.answer === undefined || answer.answer === '';
      }
      
      return isUnanswered;
    });

    if (hasUnansweredRequired) {
      setServerError('يرجى الإجابة على جميع الأسئلة المطلوبة');
      setFormState('error');
      return;
    }

    setFormState('loading');
    setServerError('');

    try {
      const response = await submitJoinRequest(data);
      setFormState('success');
      setSuccessData(response);
      onSuccess?.(response);
      
      // Reset form after successful submission
      reset();
      // Reset group selection after successful submission
      if (groups.length > 1) {
        setSelectedGroup(null);
      }
    } catch (error) {
      setFormState('error');
      const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred';
      setServerError(errorMessage);
      onError?.(errorMessage);
    }
  };

  const handleReset = () => {
    reset();
    setFormState('idle');
    setServerError('');
    setSuccessData(null);
    setGroupError('');
    // Reset group selection based on available groups
    if (groups.length === 1) {
      setSelectedGroup(groups[0] || null);
    } else {
      setSelectedGroup(null);
    }
  };

  const handleSubmitAnother = () => {
    setFormState('idle');
    setSuccessData(null);
    setServerError('');
  };

  // Format name input to ensure proper capitalization and support Arabic
  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Remove non-alphabetic characters except spaces (support Arabic and English)
    const cleanValue = value.replace(/[^a-zA-Z\s\u0600-\u06FF]/g, '');
    setValue('fullName', cleanValue);
  };

  // Handle dynamic question answer changes
  const handleAnswerChange = (questionId: number, value: string | boolean) => {
    const currentAnswers = watch('answers');
    const answerIndex = currentAnswers.findIndex(a => a.questionId === questionId);
    
    if (answerIndex >= 0) {
      // Update existing answer
      const updatedAnswers = currentAnswers.map(answer => 
        answer.questionId === questionId ? { ...answer, answer: value } : answer
      );
      setValue('answers', updatedAnswers);
    } else {
      // Add new answer if it doesn't exist
      const updatedAnswers = [...currentAnswers, { questionId, answer: value }];
      setValue('answers', updatedAnswers);
    }
  };

  // Get answer value by questionId
  const getAnswerValue = (questionId: number): string | boolean | null => {
    const currentAnswers = watch('answers');
    const answer = currentAnswers.find(a => a.questionId === questionId);
    return answer?.answer as string | boolean | null;
  };

  if (formState === 'success' && successData) {
    return (
      <div className="join-form-container">
        <div className="success-message">
          <div className="alert alert-success">
            <span>✓</span>
            <div>
              <strong>تم إرسال الطلب بنجاح!</strong>
              <p>تم استلام طلب انضمامك وهو قيد المراجعة.</p>
            </div>
          </div>
          
          <div className="success-details">
            <h3>تفاصيل الطلب:</h3>
            <p><strong>الاسم:</strong> {successData.fullName}</p>
            <p><strong>تاريخ الميلاد:</strong> {formatDate(successData.birthDate)}</p>
            <p><strong>الحالة:</strong> {successData.status}</p>
            <p><strong>تاريخ الإرسال:</strong> {formatDate(successData.createdAt)}</p>
          </div>

          <div className="success-actions">
            <button 
              type="button" 
              className="btn btn-primary"
              onClick={handleSubmitAnother}
            >
              تقديم طلب آخر
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (isLoadingQuestions) {
    return (
      <div className="join-form-container">
        <div className="loading-message">
          <span className="spinner"></span>
          جاري تحميل النموذج...
        </div>
      </div>
    );
  }

  return (
    <div className="join-form-container">
      <div className="form-header">
        <div className="header-icon">
          <img src="/estaqem_icon.svg" alt="استقم" width="80" height="80" />
        </div>
        <h1>طلب الانضمام</h1>
        <p>يرجى ملء النموذج أدناه لتقديم طلب انضمامك إلى مجتمع المسجد.</p>
      </div>

      <form 
        onSubmit={handleSubmit(onSubmit)} 
        className="join-form" 
        noValidate
      >
        {/* Server Error Display */}
        {serverError && (
          <div className="alert alert-error">
            <span>⚠</span>
            <div>
              <strong>خطأ:</strong> {serverError}
            </div>
          </div>
        )}

        {/* Group Selection (only when multiple groups) */}
        {groups.length > 1 && (
          <div className="form-group">
            <label htmlFor="group" className="form-label">
              المجموعة
            </label>
            <select
              id="group"
              className="form-input"
              value={selectedGroup ?? ''}
              onChange={(e) => {
                setSelectedGroup(e.target.value || null);
                setGroupError('');
              }}
              disabled={formState === 'loading'}
            >
              <option value="">اختر المجموعة</option>
              {groups.map(group => (
                <option key={group} value={group}>{group}</option>
              ))}
            </select>
            {groupError && (
              <div className="form-error">
                <span>⚠</span>
                {groupError}
              </div>
            )}
          </div>
        )}

        {/* Full Name Field */}
        <div className="form-group">
          <label htmlFor="fullName" className="form-label">
            الاسم الكامل *
          </label>
          <input
            id="fullName"
            type="text"
            className={`form-input ${errors.fullName ? 'error' : ''}`}
            placeholder="أدخل اسمك الكامل"
            {...register('fullName', {
              required: 'الاسم الكامل مطلوب',
              minLength: {
                value: 2,
                message: 'الاسم الكامل يجب أن يكون على الأقل حرفين'
              },
              maxLength: {
                value: 100,
                message: 'الاسم الكامل يجب ألا يتجاوز 100 حرف'
              },
              pattern: {
                value: /^[a-zA-Z\s\u0600-\u06FF]+$/,
                message: 'الاسم الكامل يجب أن يحتوي على أحرف ومسافات فقط'
              },
              onChange: handleNameChange
            })}
            disabled={formState === 'loading'}
            autoComplete="name"
          />
          {errors.fullName && (
            <div className="form-error">
              <span>⚠</span>
              {errors.fullName.message}
            </div>
          )}
        </div>

        {/* Phone Number Field */}
        <div className="form-group">
          <label htmlFor="phoneNumber" className="form-label">
            رقم الهاتف *
          </label>
          <input
            id="phoneNumber"
            type="text"
            className={`form-input ${errors.phoneNumber ? 'error' : ''}`}
            placeholder="أدخل رقم الهاتف"
            {...register('phoneNumber', {
              required: 'رقم الهاتف مطلوب',
              pattern: {
                value: /^[0-9]+$/,
                message: 'رقم الهاتف يجب أن يكون أرقام فقط'
              }
            })}
            disabled={formState === 'loading'}
            autoComplete="tel"
          />
          {errors.phoneNumber && (
            <div className="form-error">
              <span>⚠</span>
              {errors.phoneNumber.message}
            </div>
          )}
        </div>

        {/* Birth Date Field */}
        <div className="form-group">
          <label htmlFor="birthDate" className="form-label">
            تاريخ الميلاد *
          </label>
          <input
            id="birthDate"
            type="date"
            className={`form-input ${errors.birthDate ? 'error' : ''}`}
            {...register('birthDate', {
              required: 'تاريخ الميلاد مطلوب',
              validate: (value) => {
                if (!value) return 'تاريخ الميلاد مطلوب';
                
                const birthDate = new Date(value);
                const today = new Date();
                const minDate = new Date();
                minDate.setFullYear(today.getFullYear() - 1);

                if (isNaN(birthDate.getTime())) {
                  return 'يرجى إدخال تاريخ صحيح';
                }
                if (birthDate > today) {
                  return 'تاريخ الميلاد لا يمكن أن يكون في المستقبل';
                }
                if (birthDate > minDate) {
                  return 'يجب أن يكون عمرك 1 سنة على الأقل للانضمام';
                }
                return true;
              }
            })}
            disabled={formState === 'loading'}
            max={new Date().toISOString().split('T')[0]}
          />
          {errors.birthDate && (
            <div className="form-error">
              <span>⚠</span>
              {errors.birthDate.message}
            </div>
          )}
        </div>

        {/* Dynamic Questions (filtered by selected group) */}
        {(selectedGroup || groups.length <= 1) && (
          (selectedGroup ? questions.filter(q => q.group === selectedGroup) : questions).map((question, index) => (
          <div key={question.id} className="form-group">
            <label htmlFor={`question-${question.id}`} className="form-label">
              {question.question} {question.isRequired && '*'}
            </label>
            {question.description && (
              <p className="question-description">{question.description}</p>
            )}
            
            {question.type === QuestionType.TEXT ? (
              <input
                id={`question-${question.id}`}
                type="text"
                className={`form-input ${errors.answers?.[index] ? 'error' : ''}`}
                placeholder="أدخل إجابتك"
                value={String(getAnswerValue(question.id) || '')}
                onChange={(e) => {
                  handleAnswerChange(question.id, e.target.value);
                }}
                disabled={formState === 'loading'}
              />
            ) : question.type === QuestionType.BOOLEAN ? (
              <div className="boolean-input">
                <label className="radio-label">
                  <input
                    type="radio"
                    name={`question-${question.id}`}
                    checked={getAnswerValue(question.id) === true}
                    onChange={() => {
                      handleAnswerChange(question.id, true);
                    }}
                    disabled={formState === 'loading'}
                  />
                  نعم
                </label>
                <label className="radio-label">
                  <input
                    type="radio"
                    name={`question-${question.id}`}
                    checked={getAnswerValue(question.id) === false}
                    onChange={() => {
                      handleAnswerChange(question.id, false);
                    }}
                    disabled={formState === 'loading'}
                  />
                  لا
                </label>
              </div>
            ) : null}
            
            {errors.answers?.[index] && (
              <div className="form-error">
                <span>⚠</span>
                {errors.answers[index]?.answer?.message}
              </div>
            )}
          </div>
          ))
        )}

        {/* Prompt to select group before showing questions */}
        {groups.length > 1 && !selectedGroup && (
          <div className="form-group">
            <p className="question-description">يرجى اختيار المجموعة لعرض الأسئلة.</p>
          </div>
        )}

        {/* Form Actions */}
        <div className="form-actions">
          <button
            type="submit"
            className="btn btn-primary"
            disabled={formState === 'loading'}
          >
            {formState === 'loading' ? (
              <>
                <span className="spinner"></span>
                جاري الإرسال...
              </>
            ) : (
              'إرسال الطلب'
            )}
          </button>
          
          <button
            type="button"
            className="btn btn-secondary"
            onClick={handleReset}
            disabled={formState === 'loading'}
          >
            إعادة تعيين
          </button>
        </div>
      </form>

      {/* Form Guidelines */}
      <div className="form-guidelines">
        <h3>إرشادات:</h3>
        <ul>
          <li>جميع الحقول المميزة بـ * مطلوبة</li>
          <li>الاسم الكامل يجب أن يحتوي على أحرف ومسافات فقط</li>
          <li>يجب أن يكون عمرك سنة واحدة على الأقل لتقديم الطلب</li>
          <li>سيتم مراجعة طلبك من قبل إدارة المسجد ويمكنك متابعة حالة الطلب من خلال الإدارة</li>
        </ul>
      </div>
    </div>
  );
};

export default JoinRequestForm; 